/**
 * Componente para configuración general del sistema de soporte
 * 
 * Referencia: sistema-tickets-despliegue.md Sección 11.5
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  RadioGroup,
  Radio,
  FormControl
} from '@mui/material';
import axios from 'axios';
import { ErpContext } from '../../../context/ErpContext';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const ConfiguracionGeneralComponent = () => {
  const { serverURL } = useContext(ErpContext);
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    prefijoTicket: '',
    maxAttachmentSizeMb: 50,
    maxAttachmentsPerMessage: 5,
    diasAutocierreResuelto: 30,
    diasAutocierrePendienteValidacion: 7,
    tituloObligatorio: true,
    descripcionObligatoria: true,
    categoriaObligatoria: true,
    visualizacionEstadisticas: 'solo_agentes'
  });
  
  useEffect(() => {
    cargarConfig();
  }, []);
  
  /**
   * Cargar configuración desde el backend
   */
  const cargarConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${serverURL}/soporteAPI/config`, {
        withCredentials: true
      });
      
      const configData = response.data?.config || response.data;
      
      if (configData) {
        setConfig(configData);
        setFormData({
          prefijoTicket: configData.prefijoTicket || '',
          maxAttachmentSizeMb: configData.maxAttachmentSizeMb || 50,
          maxAttachmentsPerMessage: configData.maxAttachmentsPerMessage || 5,
          diasAutocierreResuelto: configData.diasAutocierreResuelto || 30,
          diasAutocierrePendienteValidacion: configData.diasAutocierrePendienteValidacion || 7,
          tituloObligatorio: configData.tituloObligatorio !== undefined ? configData.tituloObligatorio : true,
          descripcionObligatoria: configData.descripcionObligatoria !== undefined ? configData.descripcionObligatoria : true,
          categoriaObligatoria: configData.categoriaObligatoria !== undefined ? configData.categoriaObligatoria : true,
          visualizacionEstadisticas: configData.visualizacionEstadisticas || 'solo_agentes'
        });
      }
    } catch (err) {
      console.error('Error cargando configuración:', err);
      setError(err.response?.data?.error || 'Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Manejar cambio en campos del formulario
   */
  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    // Convertir a número para campos numéricos
    if (['maxAttachmentSizeMb', 'maxAttachmentsPerMessage', 'diasAutocierreResuelto', 
         'diasAutocierrePendienteValidacion'].includes(field)) {
      const numValue = value === '' ? 0 : parseInt(value, 10);
      setFormData(prev => ({ ...prev, [field]: isNaN(numValue) ? 0 : numValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  /**
   * Validar formulario
   */
  const validar = () => {
    const newErrors = {};
    
    // Validar prefijo
    if (!formData.prefijoTicket || !formData.prefijoTicket.trim()) {
      newErrors.prefijoTicket = 'El prefijo es obligatorio';
    } else if (formData.prefijoTicket.length > 4) {
      newErrors.prefijoTicket = 'El prefijo debe tener máximo 4 caracteres';
    } else if (!/^[A-Za-z]+$/.test(formData.prefijoTicket)) {
      newErrors.prefijoTicket = 'El prefijo solo puede contener letras';
    }
    
    // Validar valores numéricos
    if (formData.maxAttachmentSizeMb <= 0) {
      newErrors.maxAttachmentSizeMb = 'Debe ser mayor a 0';
    }
    
    if (formData.maxAttachmentsPerMessage <= 0) {
      newErrors.maxAttachmentsPerMessage = 'Debe ser mayor a 0';
    }
    
    if (formData.diasAutocierreResuelto < 0) {
      newErrors.diasAutocierreResuelto = 'Debe ser mayor o igual a 0';
    }
    
    if (formData.diasAutocierrePendienteValidacion < 0) {
      newErrors.diasAutocierrePendienteValidacion = 'Debe ser mayor o igual a 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Guardar configuración
   */
  const handleGuardar = async () => {
    if (!validar()) {
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Convertir prefijo a mayúsculas
      const dataToSend = {
        ...formData,
        prefijoTicket: formData.prefijoTicket.toUpperCase()
      };
      
      await axios.patch(
        `${serverURL}/soporteAPI/config`,
        dataToSend,
        { withCredentials: true }
      );
      
      setSuccess('Configuración guardada correctamente');
      cargarConfig(); // Recargar para obtener datos actualizados
    } catch (err) {
      console.error('Error guardando configuración:', err);
      setError(err.response?.data?.error || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Configuración General</Typography>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={cargarConfig}
            disabled={loading || saving}
          >
            Recargar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardar}
            disabled={saving}
          >
            Guardar
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Stack spacing={4}>
        {/* Sección: Numeración */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Numeración de Tickets
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Prefijo de Ticket"
                value={formData.prefijoTicket}
                onChange={handleChange('prefijoTicket')}
                error={!!errors.prefijoTicket}
                helperText={errors.prefijoTicket || 'Prefijo para números de ticket (1-4 caracteres, solo letras). Ej: DEMO'}
                disabled={saving}
                inputProps={{ maxLength: 4 }}
                placeholder="DEMO"
              />
            </Grid>
          </Grid>
        </Box>
        
        {/* Sección: Adjuntos */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Configuración de Adjuntos
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Tamaño Máximo por Archivo (MB)"
                value={formData.maxAttachmentSizeMb}
                onChange={handleChange('maxAttachmentSizeMb')}
                error={!!errors.maxAttachmentSizeMb}
                helperText={errors.maxAttachmentSizeMb || 'Tamaño máximo permitido para cada archivo adjunto'}
                disabled={saving}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Máximo de Adjuntos por Mensaje"
                value={formData.maxAttachmentsPerMessage}
                onChange={handleChange('maxAttachmentsPerMessage')}
                error={!!errors.maxAttachmentsPerMessage}
                helperText={errors.maxAttachmentsPerMessage || 'Número máximo de archivos que se pueden adjuntar en un mensaje'}
                disabled={saving}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </Box>
        
        {/* Sección: Autocierre */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Autocierre de Tickets
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Días para Autocierre (Resuelto)"
                value={formData.diasAutocierreResuelto}
                onChange={handleChange('diasAutocierreResuelto')}
                error={!!errors.diasAutocierreResuelto}
                helperText={errors.diasAutocierreResuelto || 'Días después de resuelto para cerrar automáticamente y permitir reapertura (0 = desactivado)'}
                disabled={saving}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Días para Autocierre (Pendiente Validación)"
                value={formData.diasAutocierrePendienteValidacion}
                onChange={handleChange('diasAutocierrePendienteValidacion')}
                error={!!errors.diasAutocierrePendienteValidacion}
                helperText={errors.diasAutocierrePendienteValidacion || 'Días después de pendiente validación para resolver automáticamente (0 = desactivado)'}
                disabled={saving}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Box>
        
        {/* Sección: Validaciones */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Validaciones de Formularios
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.tituloObligatorio}
                  onChange={handleChange('tituloObligatorio')}
                  disabled={saving}
                />
              }
              label="Título obligatorio al crear ticket"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.descripcionObligatoria}
                  onChange={handleChange('descripcionObligatoria')}
                  disabled={saving}
                />
              }
              label="Descripción obligatoria al crear ticket"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.categoriaObligatoria}
                  onChange={handleChange('categoriaObligatoria')}
                  disabled={saving}
                />
              }
              label="Categoría obligatoria al crear ticket"
            />
          </Stack>
        </Box>
        
        {/* Sección: Métricas Soporte */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Métricas Soporte
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
              Visualización de Estadísticas
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Esta opción permite cambiar qué usuarios aparecen a la hora de filtrar en las métricas.
              Los administradores pueden ver métricas generales o individuales según esta configuración.
              Los agentes siempre verán exclusivamente sus propias métricas.
            </Typography>
            <FormControl component="fieldset" disabled={saving} fullWidth>
              <RadioGroup
                value={formData.visualizacionEstadisticas}
                onChange={(e) => setFormData(prev => ({ ...prev, visualizacionEstadisticas: e.target.value }))}
              >
                <FormControlLabel
                  value="solo_agentes"
                  control={<Radio />}
                  label="Solo Agentes"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 2, display: 'block' }}>
                  Los administradores pueden ver métricas generales o métricas individuales SOLO de los agentes.
                </Typography>
                <FormControlLabel
                  value="administradores_y_agentes"
                  control={<Radio />}
                  label="Administradores y Agentes"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                  Los administradores pueden ver métricas generales o métricas individuales de agentes o administradores.
                </Typography>
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ConfiguracionGeneralComponent;

